import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import { useCountUp } from "./useCountUp";
import {
  MonthlyTrend,
  AdoptionChart,
  ScoreDistribution,
  OfficerDeltas,
  EngagementBars,
  C,
} from "./Charts";
import { CaseList } from "./CaseList";
import { CaseViewer } from "./CaseViewer";
import type { ChecklistCaseDetail, StudySummary } from "./types";
import {
  bucketShares,
  casesPerGroup,
  formatP,
  groupMean,
  monthDeltas,
  monthMatchedDelta,
  pairedOfficers,
  welch,
} from "./stats";

// Bundle URL goes through our backend so the S3 bucket can stay private
// (block-public-access enabled). The backend reads the JSON with server
// credentials and streams it to the browser. Mirrors the trial-findings
// proxy pattern.
const STUDY_DATA_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api/manchester-study-data`;

// Scores across the department dropped sharply starting this month, for
// checklist and non-checklist cases alike.
const SCORE_SHIFT_MONTH = "2026-07";
// The two months of meaningful checklist adoption.
const EARLY_MONTHS = ["2025-11", "2025-12"];

type StudyBundle = {
  generated_at: string;
  snapshot_db: string;
  agency_id: string;
  study_start_date: string;
  summary: StudySummary;
  cases: ChecklistCaseDetail[];
};

const SECTIONS: { id: string; label: string }[] = [
  { id: "abstract", label: "Abstract" },
  { id: "sec-1", label: "1. Background" },
  { id: "sec-2", label: "2. Methodology" },
  { id: "sec-3", label: "3. Results" },
  { id: "sec-4", label: "4. Case index" },
  { id: "sec-5", label: "5. Discussion" },
  { id: "sec-6", label: "6. Statistical detail" },
];

const signed = (v: number, digits = 2) => `${v >= 0 ? "+" : ""}${v.toFixed(digits)}`;
const pct = (v: number, digits = 0) => `${v.toFixed(digits)}%`;

// Every number the narrative quotes, derived once from the bundle.
function deriveStats(bundle: StudyBundle) {
  const { summary } = bundle;
  const withRow = summary.aggregate.find((r) => r.has_checklist);
  const withoutRow = summary.aggregate.find((r) => !r.has_checklist);
  const withN = withRow?.n ?? 0;
  const withoutN = withoutRow?.n ?? 0;
  const totalN = withN + withoutN;
  const test = welch(withRow, withoutRow);
  const delta = (withRow?.mean ?? 0) - (withoutRow?.mean ?? 0);
  const aggSignificant = !!test && test.p < 0.05;

  const months = monthDeltas(summary.byMonth);
  const matched = monthMatchedDelta(summary.byMonth);
  const early = months.filter((m) => EARLY_MONTHS.includes(m.month));
  const later = months.filter((m) => m.month > EARLY_MONTHS[EARLY_MONTHS.length - 1]);
  const laterNegative = later.filter((m) => m.delta < 0).length;
  const laterWithMin = later.length ? Math.min(...later.map((m) => m.withN)) : 0;
  const laterWithMax = later.length ? Math.max(...later.map((m) => m.withN)) : 0;
  const earlyWithN = early.reduce((s, m) => s + m.withN, 0);

  const beforeShift = groupMean(summary.byMonth, false, (m) => m < SCORE_SHIFT_MONTH);
  const afterShift = groupMean(summary.byMonth, false, (m) => m >= SCORE_SHIFT_MONTH);
  const hasShift = afterShift.n > 0 && beforeShift.mean - afterShift.mean >= 5;

  const paired = pairedOfficers(summary.byOfficer, 1);
  const paired2 = summary.byOfficer.filter((o) => o.with_n >= 2 && o.without_n >= 2);
  const paired2Negative = paired2.filter((o) => o.delta < 0).length;

  const sugWith = summary.suggestions.find((r) => r.has_checklist);
  const sugWithout = summary.suggestions.find((r) => !r.has_checklist);
  const rate = (r?: { implemented: number; total_suggestions: number }) =>
    r && r.total_suggestions > 0 ? (r.implemented / r.total_suggestions) * 100 : 0;
  const withRate = rate(sugWith);
  const withoutRate = rate(sugWithout);
  const ratio = withoutRate > 0 ? withRate / withoutRate : 0;
  const perCase = (r?: { total_suggestions: number; cases: number }) =>
    r && r.cases > 0 ? r.total_suggestions / r.cases : 0;
  const withPerCase = perCase(sugWith);
  const withoutPerCase = perCase(sugWithout);

  // Adoption: ignore the launch stub month and the current partial month.
  const endDate = bundle.generated_at.slice(0, 10);
  const adoption = summary.adoption
    .filter((a) => a.total_cases >= 20 && a.month < endDate.slice(0, 7))
    .map((a) => ({ ...a, share: (a.with_checklist / a.total_cases) * 100 }));
  const peak = adoption.reduce<(typeof adoption)[number] | null>(
    (best, a) => (!best || a.share > best.share ? a : best),
    null
  );
  const latest = adoption[adoption.length - 1] ?? null;
  const afterEarly = adoption.filter((a) => a.month > EARLY_MONTHS[EARLY_MONTHS.length - 1]);
  const afterEarlyMax = afterEarly.length ? Math.max(...afterEarly.map((a) => a.share)) : 0;

  const templates = [...summary.byTemplate].sort((a, b) => b.n - a.n);
  const supervisor = summary.byTemplate.find((t) => t.template_name.includes("Supervisor"));

  const pooledSd =
    withRow && withoutRow && totalN > 2
      ? Math.sqrt(
          ((withN - 1) * withRow.sd ** 2 + (withoutN - 1) * withoutRow.sd ** 2) / (totalN - 2)
        )
      : 0;
  const need2 = casesPerGroup(pooledSd, 2);
  const need3 = casesPerGroup(pooledSd, 3);
  const minDetectable = test ? (1.96 + 0.8416) * test.se : 0;

  const earlyRange = early.length
    ? `${Math.floor(Math.min(...early.map((m) => m.delta)))} to ${Math.ceil(
        Math.max(...early.map((m) => m.delta))
      )}`
    : "";

  return {
    withRow,
    withoutRow,
    withN,
    withoutN,
    totalN,
    test,
    delta,
    aggSignificant,
    months,
    matched,
    early,
    earlyRange,
    earlyWithN,
    later,
    laterNegative,
    laterWithMin,
    laterWithMax,
    beforeShift,
    afterShift,
    hasShift,
    paired,
    paired2,
    paired2Negative,
    sugWith,
    sugWithout,
    withRate,
    withoutRate,
    ratio,
    withPerCase,
    withoutPerCase,
    endDate,
    peak,
    latest,
    afterEarlyMax,
    templates,
    supervisor,
    pooledSd,
    need2,
    need3,
    minDetectable,
  };
}

type Stats = ReturnType<typeof deriveStats>;

export default function ManchesterPDChecklistResults() {
  const [bundle, setBundle] = useState<StudyBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get<StudyBundle>(STUDY_DATA_URL, { withCredentials: false })
      .then((r) => {
        if (!cancelled) setBundle(r.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || `Failed to load study data from ${STUDY_DATA_URL}.`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCase = useMemo(
    () => bundle?.cases.find((c) => c.id === selectedCaseId) ?? null,
    [bundle, selectedCaseId]
  );
  const stats = useMemo(() => (bundle ? deriveStats(bundle) : null), [bundle]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!bundle || !stats) return null;

  const { summary, cases } = bundle;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <StudyHeader bundle={bundle} stats={stats} />
      <div className="max-w-5xl mx-auto px-6 pb-24">
        <TableOfContents />
        <Abstract stats={stats} />
        <Background summary={summary} />
        <Methodology bundle={bundle} stats={stats} />
        <Results summary={summary} stats={stats} />
        <CaseIndex cases={cases} onSelect={setSelectedCaseId} />
        <Discussion stats={stats} />
        <StatisticalDetail stats={stats} />
      </div>
      {selectedCase && (
        <CaseViewer detail={selectedCase} onClose={() => setSelectedCaseId(null)} />
      )}
    </div>
  );
}

// ================== Header ====================

function StudyHeader({ bundle, stats }: { bundle: StudyBundle; stats: Stats }) {
  const officers = bundle.summary.officers;
  return (
    <header className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white border-b border-slate-900">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <BackgroundGrid />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 py-14 md:py-20">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-blue-300/90 font-medium mb-4">
          <span className="inline-block w-6 h-px bg-blue-400/60" />
          Research study · Police Narratives AI
        </div>
        <h1 className="text-3xl md:text-[40px] font-semibold tracking-tight text-white leading-snug max-w-3xl">
          Case checklists at Manchester Police Department: Do structured investigative
          checklists produce higher-quality reports?
        </h1>

        <dl className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 text-sm border-t border-white/10 pt-8">
          <MetaField label="Prepared for" value="Manchester Police Department" />
          <MetaField label="Agency ID" value={bundle.agency_id} mono />
          <MetaField label="Study window" value={`${bundle.study_start_date} → ${stats.endDate}`} />
          <MetaField label="Report date" value={stats.endDate} />
          <MetaField
            label="Cases analysed"
            value={`${stats.totalN.toLocaleString()} scored, non-discarded`}
          />
          <MetaField
            label="Officers"
            value={
              officers
                ? `${officers.total} (${officers.both_groups} in both groups)`
                : `${stats.paired.n} in both groups`
            }
          />
          <MetaField label="Quality metric" value="case_scores.score (0 to 100)" mono />
        </dl>
      </div>
    </header>
  );
}

function MetaField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.14em] text-blue-300/80 font-medium">
        {label}
      </dt>
      <dd
        className={`mt-1 text-slate-100 ${
          mono ? "font-mono text-[12.5px] break-all" : "text-[13.5px]"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function BackgroundGrid() {
  return (
    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <pattern id="mpd-grid" width="48" height="48" patternUnits="userSpaceOnUse">
          <path
            d="M 48 0 L 0 0 0 48"
            fill="none"
            stroke="rgba(96, 165, 250, 0.35)"
            strokeWidth="0.5"
          />
        </pattern>
        <radialGradient id="mpd-fade" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="white" stopOpacity="0.55" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#mpd-grid)" />
      <rect width="100%" height="100%" fill="url(#mpd-fade)" opacity="0.4" />
    </svg>
  );
}

// ================== Table of Contents ====================

function TableOfContents() {
  return (
    <nav className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-white/85 backdrop-blur border-b border-slate-200">
      <ol className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <a href={`#${s.id}`} className="hover:text-blue-700 transition-colors">
              {s.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ================== Section shell ====================

function Section({
  id,
  number,
  title,
  subtitle,
  children,
}: {
  id?: string;
  number?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="pt-14 md:pt-20 scroll-mt-16">
      <div className="flex items-baseline gap-3 mb-2">
        {number && (
          <span className="text-sm font-mono text-slate-400 tabular-nums">{number}</span>
        )}
        <h2 className="text-2xl md:text-[28px] font-semibold tracking-tight text-slate-900 leading-tight">
          {title}
        </h2>
      </div>
      {subtitle && (
        <p className="text-slate-600 leading-relaxed max-w-3xl mb-6">{subtitle}</p>
      )}
      {children}
    </section>
  );
}

function SubSection({
  number,
  title,
  subtitle,
  children,
}: {
  number: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-10">
      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-xs font-mono text-slate-400 tabular-nums">{number}</span>
        <h3 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
      </div>
      {subtitle && (
        <p className="text-slate-600 text-sm leading-relaxed max-w-3xl mb-4">{subtitle}</p>
      )}
      {children}
    </div>
  );
}

// ================== Shared narrative fragments ====================

function earlyDeltasText(s: Stats): string {
  return s.early.map((m) => `${signed(m.delta, 1)} pts in ${m.month}`).join(", ");
}

function shiftText(s: Stats): string {
  return `Scores across the department fell from ${SCORE_SHIFT_MONTH} onward (non-checklist mean ${s.beforeShift.mean.toFixed(
    1
  )} before, ${s.afterShift.mean.toFixed(1)} after), for checklist and non-checklist cases alike.`;
}

// ================== Abstract ====================

function Abstract({ stats: s }: { stats: Stats }) {
  const n = useCountUp(s.totalN, 1200);
  const ratioAnimated = useCountUp(s.ratio, 1500, 2);
  const deltaAnimated = useCountUp(s.delta, 1200, 2);
  const earlyShare = s.withN ? (s.earlyWithN / s.withN) * 100 : 0;

  return (
    <Section id="abstract" title="Abstract">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard
          label="N (scored cases)"
          value={n.toLocaleString()}
          sub={`${s.withN} with · ${s.withoutN} without`}
        />
        <StatCard
          label="Mean score Δ"
          value={`${deltaAnimated >= 0 ? "+" : ""}${deltaAnimated.toFixed(2)} pts`}
          sub={
            s.test
              ? `raw, ${formatP(s.test.p)} · ${signed(s.matched)} month-matched`
              : undefined
          }
        />
        <StatCard
          label="Implementation ratio"
          value={`${ratioAnimated.toFixed(2)}×`}
          sub={`${s.withRate.toFixed(2)}% vs ${s.withoutRate.toFixed(2)}%`}
        />
        {s.peak && (
          <StatCard
            label="Peak adoption"
            value={pct(s.peak.share)}
            sub={
              s.latest
                ? `${s.peak.month}; ${pct(s.latest.share)} in ${s.latest.month}`
                : s.peak.month
            }
          />
        )}
      </div>

      <div className="prose prose-slate max-w-none prose-p:leading-relaxed prose-p:text-slate-700">
        <p>
          Manchester PD piloted a <b>Case Checklists</b> feature on 2025-10-28 providing officers a
          structured, note-capable investigative prompt list. We evaluated whether cases completed
          with a checklist received higher AI-assessed quality scores than cases written without
          one. Across <b>{s.totalN.toLocaleString()}</b> scored, non-discarded cases written
          during the study window, checklist cases averaged{" "}
          {(s.withRow?.mean ?? 0).toFixed(2)} against {(s.withoutRow?.mean ?? 0).toFixed(2)}, a
          raw difference of <b>{signed(s.delta)} points</b>
          {s.test && ` (Welch's t ≈ ${s.test.t.toFixed(2)}, ${formatP(s.test.p)})`}.{" "}
          {!s.aggSignificant
            ? "That difference is not statistically distinguishable from zero."
            : s.hasShift
              ? `That raw gap is not evidence of a checklist effect. ${shiftText(s)} ${pct(
                  earlyShare
                )} of checklist cases were written in ${EARLY_MONTHS.join(" and ")}, before that drop. Comparing each month's checklist cases with the same month's non-checklist cases leaves a difference of only `
              : "The difference is statistically significant, but officers choose when to use a checklist, so it cannot be attributed to the checklist itself (§5.3). The month-matched difference is "}
          {s.aggSignificant && (
            <>
              <b>{signed(s.matched)} points</b>.
            </>
          )}
        </p>
        <p>
          Two secondary findings are reported. First, the rate at which officers implement
          AI-generated suggestions is <b>{s.ratio.toFixed(2)}×</b> higher on checklist cases (
          {s.withRate.toFixed(2)}% vs {s.withoutRate.toFixed(2)}%), though it rests on small counts
          ({s.sugWith?.implemented ?? 0} and {s.sugWithout?.implemented ?? 0} implemented
          suggestions). Second, a positive score difference ({earlyDeltasText(s)}) is observed
          during the two months of meaningful adoption, but the monthly checklist sample shrinks
          sharply as adoption declines
          {s.peak && s.latest && ` from ${pct(s.peak.share)} to ${pct(s.latest.share)}`}, which
          limits the interpretability of later months.
        </p>
      </div>
    </Section>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-xl md:text-2xl font-semibold text-slate-900 mt-0.5 tabular-nums">
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

// ================== Background ====================

function Background({ summary }: { summary: StudySummary }) {
  const coverage = summary.scoreCoverage;
  return (
    <Section
      id="sec-1"
      number="1"
      title="Background"
      subtitle="Feature context, quality metric, and hypothesis."
    >
      <SubSection
        number="1.1"
        title="The Case Checklists feature"
        subtitle="Launched at Manchester PD on 2025-10-28. Officers may select from one of two agency templates. Each checklist is attached to a case, can be annotated with notes, and appears in the case's context alongside audio, transcripts, and attachments."
      >
        <Table
          columns={["Template", "Active since", "Cases in study"]}
          rows={summary.byTemplate.map((t) => [
            t.template_name,
            t.template_name.includes("Supervisor") ? "2025-10-28" : "2025-11-05",
            String(t.n),
          ])}
        />
      </SubSection>

      <SubSection
        number="1.2"
        title="Quality metric"
        subtitle="Each report receives an integer 0–100 quality score produced by the AI reviewer on the narrative (case_scores.score). We used the latest non-archived score per case."
      >
        <div className="text-sm text-slate-600">
          Of all scored cases in the database, most (
          {coverage
            ? `${coverage.single_score.toLocaleString()} of ${coverage.scored_cases.toLocaleString()}`
            : "22,660 of 22,757"}
          ) have exactly one non-archived score. For the handful with multiple non-archived
          entries, the most recent was retained.
        </div>
      </SubSection>

      <SubSection number="1.3" title="Hypothesis">
        <blockquote className="border-l-2 border-slate-300 pl-4 text-slate-700 italic">
          Cases written with the aid of a case checklist will receive higher quality scores than
          cases written without one, both at the agency level and at the individual-officer level.
        </blockquote>
      </SubSection>
    </Section>
  );
}

// ================== Methodology ====================

function Methodology({ bundle, stats: s }: { bundle: StudyBundle; stats: Stats }) {
  const imbalance = s.withN ? s.withoutN / s.withN : 0;

  return (
    <Section
      id="sec-2"
      number="2"
      title="Methodology"
      subtitle="Population definition, grouping rule, and analytic approach."
    >
      <SubSection number="2.1" title="Case population">
        <ul className="list-disc pl-5 text-slate-700 text-sm space-y-1.5 leading-relaxed">
          <li>
            <b>Agency:</b> Manchester Police Department only, via <code>users.agency_id</code>.
          </li>
          <li>
            <b>Window:</b> cases with <code>created_at ≥ {bundle.study_start_date}</code>{" "}
            (checklist launch date) through {s.endDate}. This excludes pre-launch cases from
            either group to remove a baseline confounder.
          </li>
          <li>
            <b>Inclusions:</b> only non-discarded cases (<code>discarded_at IS NULL</code>) with
            at least one non-archived score.
          </li>
          <li>
            <b>Final N:</b> {s.totalN.toLocaleString()} cases ({s.withN} with checklist,{" "}
            {s.withoutN.toLocaleString()} without) written by Manchester officers.
          </li>
        </ul>
      </SubSection>

      <SubSection
        number="2.2"
        title="Grouping"
        subtitle="A case is classified as 'with checklist' if it has any row in case_checklists linked to it, and 'without checklist' otherwise."
      >
        <></>
      </SubSection>

      <SubSection number="2.3" title="Analyses">
        <ol className="list-decimal pl-5 text-slate-700 text-sm space-y-1.5 leading-relaxed">
          <li>Aggregate comparison: mean, median, SD, and score-bucket distribution per group.</li>
          <li>
            Month-matched comparison: each month's with-minus-without difference, weighted by that
            month's checklist cases. Controls for changes in the score level over time.
          </li>
          <li>
            Per-officer paired analysis: mean(with) − mean(without) per officer, averaged across
            officers present in both groups. Controls for individual writing skill.
          </li>
          <li>Monthly trend: per-month means for each group.</li>
          <li>
            Engagement on AI suggestions: suggestions-per-case and share of AI suggestions
            marked implemented.
          </li>
          <li>Per-template breakdown: scores by checklist template used.</li>
        </ol>
      </SubSection>

      <SubSection number="2.4" title="Metric caveats">
        <ul className="list-disc pl-5 text-slate-700 text-sm space-y-1.5 leading-relaxed">
          <li>
            <b>Score is AI-generated.</b> It is a proxy for report quality rather than an
            independent ground-truth rating. A change in the score level during the study window
            affects both groups equally only if both are spread evenly over time. They are not:
            checklist cases cluster in the first months of the study
            {s.hasShift && `, and scores fell for everyone from ${SCORE_SHIFT_MONTH}`}. The
            month-matched comparison (§3.3) removes this.
          </li>
          <li>
            <b>Sample imbalance.</b> The "without" group is ~{Math.round(imbalance)}× larger than
            the "with" group. Detecting a 2-point effect at 95% confidence and 80% power would take
            roughly {s.need2.toLocaleString()} checklist cases, about{" "}
            {s.withN ? Math.round(s.need2 / s.withN) : 0}× the current sample.
          </li>
          <li>
            <b>Non-random assignment.</b> Officers self-select when to use a checklist, so
            observed effects cannot be cleanly attributed to the intervention.
          </li>
        </ul>
      </SubSection>
    </Section>
  );
}

// ================== Results ====================

function Results({ summary, stats }: { summary: StudySummary; stats: Stats }) {
  return (
    <Section
      id="sec-3"
      number="3"
      title="Results"
      subtitle="Aggregate, distributional, temporal, per-officer, engagement, and per-template analyses."
    >
      <AggregateSub stats={stats} />
      <DistributionSub summary={summary} stats={stats} />
      <MonthlySub summary={summary} stats={stats} />
      <AdoptionSub summary={summary} stats={stats} />
      <OfficerSub summary={summary} stats={stats} />
      <EngagementSub stats={stats} />
      <TemplateSub summary={summary} stats={stats} />
    </Section>
  );
}

function AggregateSub({ stats: s }: { stats: Stats }) {
  const { withRow, withoutRow } = s;

  return (
    <SubSection
      number="3.1"
      title="Aggregate score comparison"
      subtitle="Mean, median, and standard deviation of case quality scores across the full study window."
    >
      <div className="grid md:grid-cols-2 gap-4 items-stretch">
        <div className="border border-slate-200 rounded-md p-5 bg-white flex flex-col">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-4">
            Mean score (0 to 100)
          </div>
          <div className="flex">
            {/* Y axis */}
            <div className="relative w-8 h-52 mr-2">
              {[0, 25, 50, 75, 100].map((tick) => (
                <div
                  key={tick}
                  className="absolute right-0 text-[10px] text-slate-400 tabular-nums -translate-y-1/2"
                  style={{ bottom: `${tick}%` }}
                >
                  {tick}
                </div>
              ))}
            </div>
            {/* Plot area */}
            <div className="relative flex-1 h-52">
              {[25, 50, 75, 100].map((tick) => (
                <div
                  key={tick}
                  className="absolute inset-x-0 border-t border-dashed border-slate-200 pointer-events-none"
                  style={{ bottom: `${tick}%` }}
                />
              ))}
              <div className="absolute inset-x-0 bottom-0 border-t border-slate-300" />
              <div className="relative h-full flex gap-10 justify-center items-end px-6">
                <ScoreColumn
                  color={C.with}
                  mean={withRow?.mean ?? 0}
                  n={withRow?.n ?? 0}
                  label="With checklist"
                />
                <ScoreColumn
                  color={C.without}
                  mean={withoutRow?.mean ?? 0}
                  n={withoutRow?.n ?? 0}
                  label="Without checklist"
                />
              </div>
            </div>
          </div>
          <div className="mt-3 flex">
            <div className="w-10" />
            <div className="flex-1 flex gap-10 justify-center px-6 text-center">
              <div style={{ width: 64 }}>
                <div className="text-xs font-medium text-slate-700 leading-tight">With</div>
                <div className="text-xs font-medium text-slate-700 leading-tight">checklist</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  n = {(withRow?.n ?? 0).toLocaleString()}
                </div>
              </div>
              <div style={{ width: 64 }}>
                <div className="text-xs font-medium text-slate-700 leading-tight">Without</div>
                <div className="text-xs font-medium text-slate-700 leading-tight">checklist</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  n = {(withoutRow?.n ?? 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <StatRow label="Median" withVal={withRow?.median} withoutVal={withoutRow?.median} />
            <StatRow label="SD" withVal={withRow?.sd} withoutVal={withoutRow?.sd} />
          </div>
          {s.test && (
            <div className="mt-4 text-xs text-slate-500 border-t border-slate-100 pt-3">
              Welch's t ≈ {s.test.t.toFixed(2)}, {formatP(s.test.p)}
              {s.aggSignificant
                ? `. The raw gap is driven by when checklist cases were written; the month-matched difference is ${signed(
                    s.matched
                  )} points (§3.3).`
                : "; not statistically distinguishable from zero."}{" "}
              See §6.
            </div>
          )}
        </div>

        <div className="border border-slate-200 rounded-md p-5 bg-white">
          <Table
            columns={["Group", "n", "Mean", "Median", "SD", "Min", "Max"]}
            rows={[
              [
                "With checklist",
                String(withRow?.n ?? "n/a"),
                (withRow?.mean ?? 0).toFixed(2),
                String(withRow?.median ?? "n/a"),
                (withRow?.sd ?? 0).toFixed(2),
                String(withRow?.min ?? "n/a"),
                String(withRow?.max ?? "n/a"),
              ],
              [
                "Without checklist",
                String(withoutRow?.n ?? "n/a"),
                (withoutRow?.mean ?? 0).toFixed(2),
                String(withoutRow?.median ?? "n/a"),
                (withoutRow?.sd ?? 0).toFixed(2),
                String(withoutRow?.min ?? "n/a"),
                String(withoutRow?.max ?? "n/a"),
              ],
            ]}
          />
        </div>
      </div>
    </SubSection>
  );
}

function DistributionSub({ summary, stats: s }: { summary: StudySummary; stats: Stats }) {
  const buckets = bucketShares(summary.distribution);
  const shares = (label: string) => {
    const b = buckets.find((x) => x.label === label);
    return b ? `${b.with}% vs ${b.without}%` : "";
  };
  const describe = (label: string) => (shares(label) ? `${label} (${shares(label)})` : "");
  const lowTail = ["< 50", "50-64"].map(describe).filter(Boolean).join(", ");

  return (
    <SubSection
      number="3.2"
      title="Score distribution"
      subtitle="Share of cases in each score band, by group."
    >
      <div className="border border-slate-200 rounded-md p-5 bg-white">
        <ScoreDistribution buckets={buckets} />
        <div className="text-xs text-slate-500 mt-3 leading-relaxed">
          Shares are with checklist vs without. Checklist cases sit more in the 75–84 band (
          {shares("75-84")}) and less in the low bands: {lowTail}.
          {s.hasShift &&
            ` The without group holds most of the reports written after the ${SCORE_SHIFT_MONTH} score drop, which widens its low tail.`}
        </div>
      </div>
    </SubSection>
  );
}

function MonthlySub({ summary, stats: s }: { summary: StudySummary; stats: Stats }) {
  return (
    <SubSection
      number="3.3"
      title="Month-by-month comparison"
      subtitle={`Per-month mean scores for each group. Shaded region marks months in which checklist adoption is below 10%, i.e. samples of ${s.laterWithMin}–${s.laterWithMax} checklist cases that are unlikely to be representative.`}
    >
      <div className="border border-slate-200 rounded-md p-5 bg-white">
        <MonthlyTrend data={summary.byMonth} />
        <div className="text-xs text-slate-500 mt-3 leading-relaxed">
          In {EARLY_MONTHS.join(" and ")}, the two months of meaningful checklist adoption,
          checklist cases scored {s.early.map((m) => signed(m.delta, 1)).join(" and ")} points
          higher than non-checklist cases. From 2026-01 onward only {s.laterWithMin} to{" "}
          {s.laterWithMax} checklist cases exist per month, and the gap is negative in{" "}
          {s.laterNegative} of {s.later.length} months. These later months cannot distinguish the
          checklist's effect from selection effects in which officers or case types continued to
          use the feature.{s.hasShift && ` ${shiftText(s)}`} Weighting each month's gap by its
          checklist cases gives a month-matched difference of{" "}
          <b className="text-slate-700">{signed(s.matched)} points</b>.
        </div>
      </div>
    </SubSection>
  );
}

function AdoptionSub({ summary, stats: s }: { summary: StudySummary; stats: Stats }) {
  return (
    <SubSection
      number="3.4"
      title="Adoption trajectory"
      subtitle="Checklist usage on Manchester cases by month, in absolute counts and as a share of total monthly cases."
    >
      <div className="border border-slate-200 rounded-md p-5 bg-white">
        <AdoptionChart data={summary.adoption} />
        {s.peak && s.latest && (
          <div className="text-xs text-slate-500 mt-3 leading-relaxed">
            Adoption peaked at {pct(s.peak.share)} in {s.peak.month} and has stayed below{" "}
            {pct(Math.ceil(s.afterEarlyMax))} every month since 2026-01, at{" "}
            {pct(s.latest.share)} in {s.latest.month}. The adoption trajectory, rather than the
            checklist's direct effect on score, is the principal confound in longer-horizon
            analyses.
          </div>
        )}
      </div>
    </SubSection>
  );
}

function OfficerSub({ summary, stats: s }: { summary: StudySummary; stats: Stats }) {
  const p = s.paired;

  return (
    <SubSection
      number="3.5"
      title="Per-officer paired analysis"
      subtitle="For officers who wrote at least one case in each group, the difference between their mean with-checklist score and mean without-checklist score. Averaging across officers controls for individual writing skill."
    >
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <StatCard label="Paired officers" value={p.n.toString()} sub="≥ 1 case in each group" />
        <StatCard
          label="Mean within-officer Δ"
          value={`${signed(p.mean)} pts`}
          sub={`t ≈ ${p.t.toFixed(2)}, ${formatP(p.p)}`}
        />
        <StatCard
          label="Positive deltas"
          value={`${p.positive}/${p.n}`}
          sub={`${((p.positive / Math.max(p.n, 1)) * 100).toFixed(0)}% of officers`}
        />
      </div>
      <div className="border border-slate-200 rounded-md p-5 bg-white">
        <div className="text-sm text-slate-600 mb-3">
          Officers with at least 2 cases in each group ({s.paired2.length}). Positive bars
          indicate higher with-checklist mean; negative bars the inverse. n values reported in
          the officer label.
        </div>
        <OfficerDeltas data={summary.byOfficer} />
        <div className="text-xs text-slate-500 mt-3 leading-relaxed border-t border-slate-100 pt-3">
          Within-officer mean of {signed(p.mean)} with SD {p.sd.toFixed(2)} across {p.n} officers
          yields t ≈ {p.t.toFixed(2)}, {formatP(p.p)},{" "}
          {p.p < 0.05 ? "significant at α = 0.05" : "not significant at α = 0.05"}. Most paired
          officers have only one or two checklist cases, so each delta is noisy.
          {s.hasShift &&
            ` Officers who used checklists early had those cases scored before the ${SCORE_SHIFT_MONTH} drop while many of their other reports were scored after it, which pushes these deltas upward.`}{" "}
          {s.paired2Negative} of the {s.paired2.length} officers with two or more cases in each
          group score lower on their checklist cases.
        </div>
      </div>
    </SubSection>
  );
}

function EngagementSub({ stats: s }: { stats: Stats }) {
  const { withRate, withoutRate, withPerCase, withoutPerCase } = s;
  const perCaseDiff = withoutPerCase > 0 ? (withPerCase / withoutPerCase - 1) * 100 : 0;

  return (
    <SubSection
      number="3.6"
      title="Engagement with AI feedback"
      subtitle="Two engagement metrics derived from the suggestions table: AI suggestions generated per case, and the rate at which officers mark suggestions implemented."
    >
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-md p-5 bg-white">
          <div className="text-sm font-semibold text-slate-700 mb-3">Implementation rate</div>
          <EngagementBars
            withRate={withRate}
            withoutRate={withoutRate}
            withTotal={s.sugWith?.total_suggestions ?? 0}
            withoutTotal={s.sugWithout?.total_suggestions ?? 0}
            withImplemented={s.sugWith?.implemented ?? 0}
            withoutImplemented={s.sugWithout?.implemented ?? 0}
          />
        </div>
        <div className="border border-slate-200 rounded-md p-5 bg-white flex flex-col">
          <div className="text-sm font-semibold text-slate-700 mb-8">
            AI suggestions generated per case
          </div>
          {(() => {
            const scaleMax = Math.max(12, withPerCase, withoutPerCase);
            const ticks = [0, 3, 6, 9, 12];
            return (
              <div className="flex">
                <div className="relative w-8 h-44 mr-2">
                  {ticks.map((tick) => (
                    <div
                      key={tick}
                      className="absolute right-0 text-[10px] text-slate-400 tabular-nums -translate-y-1/2"
                      style={{ bottom: `${(tick / scaleMax) * 100}%` }}
                    >
                      {tick}
                    </div>
                  ))}
                </div>
                <div className="relative flex-1 h-44">
                  {ticks
                    .filter((t) => t > 0)
                    .map((tick) => (
                      <div
                        key={tick}
                        className="absolute inset-x-0 border-t border-dashed border-slate-200 pointer-events-none"
                        style={{ bottom: `${(tick / scaleMax) * 100}%` }}
                      />
                    ))}
                  <div className="absolute inset-x-0 bottom-0 border-t border-slate-300" />
                  <div className="relative h-full flex gap-10 justify-center items-end px-6">
                    <PerCaseBar color={C.with} value={withPerCase} scaleMax={scaleMax} />
                    <PerCaseBar color={C.without} value={withoutPerCase} scaleMax={scaleMax} />
                  </div>
                </div>
              </div>
            );
          })()}
          <div className="mt-3 flex">
            <div className="w-10" />
            <div className="flex-1 flex gap-10 justify-center px-6 text-center">
              <div style={{ width: 64 }} className="text-xs font-medium text-slate-700 leading-tight">
                <div>With</div>
                <div>checklist</div>
              </div>
              <div style={{ width: 64 }} className="text-xs font-medium text-slate-700 leading-tight">
                <div>Without</div>
                <div>checklist</div>
              </div>
            </div>
          </div>
          <div className="text-xs text-slate-500 mt-5 leading-relaxed">
            Checklist cases receive {Math.abs(perCaseDiff) < 3 ? "about the same number of" : `approximately ${Math.round(Math.abs(perCaseDiff))}% ${perCaseDiff > 0 ? "more" : "fewer"}`}{" "}
            suggestions per case ({withPerCase.toFixed(2)} vs {withoutPerCase.toFixed(2)}), so
            suggestion volume does not account for the implementation-rate difference.
          </div>
        </div>
      </div>
    </SubSection>
  );
}

function TemplateSub({ summary, stats: s }: { summary: StudySummary; stats: Stats }) {
  const main = s.templates[0];
  return (
    <SubSection
      number="3.7"
      title="Per-template comparison"
      subtitle="Score distribution by checklist template used."
    >
      <Table
        columns={["Template", "n", "Mean", "SD"]}
        rows={summary.byTemplate.map((t) => [
          t.template_name,
          String(t.n),
          t.mean?.toFixed(2) ?? "n/a",
          t.sd?.toFixed(2) ?? "n/a",
        ])}
      />
      {main && (
        <div className="text-xs text-slate-500 mt-3 leading-relaxed">
          The {main.template_name.trim()} accounts for {main.n} of {s.withN} checklist cases.
          {s.supervisor &&
            s.supervisor !== main &&
            ` The Supervisor Critical Incident Checklist sample (n=${s.supervisor.n}) is too small to support any inference; effectively all evidence in this study speaks to the ${main.template_name.trim()} template.`}
        </div>
      )}
    </SubSection>
  );
}

// ================== Case index ====================

function CaseIndex({
  cases,
  onSelect,
}: {
  cases: ChecklistCaseDetail[];
  onSelect: (id: string) => void;
}) {
  return (
    <Section
      id="sec-4"
      number="4"
      title="Case index"
      subtitle="All cases written with a checklist during the study window. Selecting a case opens the full report narrative alongside the completed checklist and officer notes."
    >
      <CaseList cases={cases} onSelect={onSelect} />
    </Section>
  );
}

// ================== Discussion ====================

function Discussion({ stats: s }: { stats: Stats }) {
  const p = s.paired;
  const noNegative = !(s.test && s.test.p < 0.05 && s.delta < 0) && !(p.p < 0.05 && p.mean < 0);

  return (
    <Section id="sec-5" number="5" title="Discussion">
      <SubSection number="5.1" title="Findings supported by the data">
        <ul className="list-disc pl-5 text-slate-700 text-sm space-y-1.5 leading-relaxed">
          {noNegative && (
            <li>
              No analysis shows a statistically significant negative effect on report quality
              associated with checklist use.
            </li>
          )}
          <li>
            During {EARLY_MONTHS.join(" and ")}, the two months of meaningful adoption, checklist
            cases scored {s.earlyRange} points higher than non-checklist cases.
          </li>
          <li>
            {p.positive} of {p.n} officers who wrote cases both ways score higher on their
            checklist cases (mean {signed(p.mean)} points), though the difference is{" "}
            {p.p < 0.05 ? "" : "not "}statistically significant ({formatP(p.p)}).
          </li>
          <li>
            Implementation of AI-generated suggestions is {s.ratio.toFixed(2)}× higher on
            checklist cases ({s.withRate.toFixed(2)}% vs {s.withoutRate.toFixed(2)}%).
          </li>
        </ul>
      </SubSection>

      <SubSection number="5.2" title="Findings not supported by the data">
        <ul className="list-disc pl-5 text-slate-700 text-sm space-y-1.5 leading-relaxed">
          {s.test && s.aggSignificant ? (
            <li>
              A checklist effect on score once timing is controlled for. The raw aggregate gap (
              {signed(s.delta)} points, {formatP(s.test.p)}) shrinks to {signed(s.matched)} points
              when each month is compared with itself.
            </li>
          ) : (
            s.test && (
              <li>
                A statistically significant aggregate lift ({formatP(s.test.p)} aggregate).
              </li>
            )
          )}
          {p.p >= 0.05 && (
            <li>A statistically significant per-officer lift ({formatP(p.p)}).</li>
          )}
          <li>
            A uniform benefit; {s.paired2Negative} of {s.paired2.length} officers with two or more
            cases in each group score lower with a checklist.
          </li>
          {s.supervisor && (
            <li>
              Any conclusion about the Supervisor Critical Incident Checklist (n=
              {s.supervisor.n}).
            </li>
          )}
        </ul>
      </SubSection>

      <SubSection number="5.3" title="Confounders and limitations">
        <ol className="list-decimal pl-5 text-slate-700 text-sm space-y-1.5 leading-relaxed">
          {s.hasShift && (
            <li>
              <b>Score level shifts over time.</b> {shiftText(s)} Checklist cases are concentrated
              in the earliest months, so any comparison that pools months overstates the checklist
              difference. The month-matched figure is the more reliable estimate.
            </li>
          )}
          <li>
            <b>Adoption collapse induces selection bias.</b> Once adoption fell below ~10%,
            remaining checklist users represent a non-random subset of the department. Negative
            monthly deltas from 2026-01 onward plausibly reflect which officers kept using the
            feature rather than its direct effect.
          </li>
          <li>
            <b>Non-randomised assignment.</b> Officers choose when to use a checklist. The
            causal effect of the checklist cannot be separated from the effect of "the kind of
            officer (or situation) that leads to using a checklist."
          </li>
          <li>
            <b>Proxy outcome.</b> The quality score is produced by the same AI system that
            generates suggestions and is not an independent rating against an external standard
            (e.g. supervisor or courtroom review).
          </li>
          <li>
            <b>Small per-officer depth.</b> With one or two checklist cases per officer typical
            and only {p.n} officers in both groups, within-officer estimates remain high-variance.
          </li>
        </ol>
      </SubSection>

      <SubSection number="5.4" title="Recommendations">
        <ol className="list-decimal pl-5 text-slate-700 text-sm space-y-1.5 leading-relaxed">
          <li>
            Investigate the adoption decline directly with officers who stopped using the feature
            before drawing a final verdict on its effect.
          </li>
          <li>
            Run a pre-specified comparison at sustained volume. A 4–8 week period at 30%+
            adoption would yield ~400+ checklist cases, enough to detect a 3-point effect (~
            {s.need3.toLocaleString()} cases per group needed). Confirming the smaller end of the{" "}
            {s.earlyRange} point effect the Nov–Dec 2025 data indicates would take ~
            {s.need2.toLocaleString()}.
          </li>
          {s.hasShift && (
            <li>
              Confirm whether the {SCORE_SHIFT_MONTH} drop in scores reflects a change to the
              scoring model or a real change in reports, and compare periods only within the same
              scoring regime.
            </li>
          )}
          <li>
            Pilot the Supervisor Critical Incident Checklist as a separate directed rollout to
            generate a dedicated sample.
          </li>
          <li>
            Report the engagement-rate lift as an outcome in its own right; it is a concrete
            behavioural change independent of the score question.
          </li>
        </ol>
      </SubSection>
    </Section>
  );
}

// ================== Statistical detail ====================

function StatisticalDetail({ stats: s }: { stats: Stats }) {
  const { withRow: w, withoutRow: o, test, paired: p } = s;

  return (
    <Section
      id="sec-6"
      number="6"
      title="Statistical detail"
      subtitle="Tests and power calculations referenced above."
    >
      {w && o && test && (
        <SubSection number="6.1" title="Welch's t (aggregate)">
          <pre className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm font-mono text-slate-800 overflow-x-auto leading-relaxed">
{`SE(Δμ) = sqrt(${w.sd.toFixed(2)}² / ${w.n} + ${o.sd.toFixed(2)}² / ${o.n}) = ${test.se.toFixed(3)}
t      = (${w.mean.toFixed(2)} − ${o.mean.toFixed(2)}) / ${test.se.toFixed(3)} = ${test.t.toFixed(3)}
df     ≈ ${Math.round(test.df)}
${formatP(test.p)} (two-tailed)`}
          </pre>
        </SubSection>
      )}

      <SubSection number="6.2" title="Month-matched difference">
        <pre className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm font-mono text-slate-800 overflow-x-auto leading-relaxed">
{`month     checklist n   Δ (with − without)
${s.months
  .map((m) => `${m.month}   ${String(m.withN).padStart(11)}   ${signed(m.delta).padStart(8)}`)
  .join("\n")}

weighted Δ = Σ(n × Δ) / Σn = ${signed(s.matched)}`}
        </pre>
      </SubSection>

      <SubSection number="6.3" title={`Per-officer paired test (n=${p.n})`}>
        <pre className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm font-mono text-slate-800 overflow-x-auto leading-relaxed">
{`mean Δ  = ${signed(p.mean)}
SD      = ${p.sd.toFixed(2)}
SE      = ${p.sd.toFixed(2)} / sqrt(${p.n}) = ${p.se.toFixed(2)}
t       = ${p.t.toFixed(2)}
${formatP(p.p)} (two-tailed)`}
        </pre>
      </SubSection>

      <SubSection number="6.4" title="Power">
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">
          At the observed {s.pooledSd.toFixed(0)}-point within-group SD, detecting a 2-point
          effect at α = 0.05 and β = 0.20 requires roughly {s.need2.toLocaleString()} cases per
          group; a 3-point effect, ~{s.need3.toLocaleString()} per group. The current {s.withN}{" "}
          checklist cases are sufficient to detect effects of about{" "}
          {Math.ceil(s.minDetectable)} points or more, so they are under-powered for the{" "}
          {s.earlyRange} point effect indicated by the {EARLY_MONTHS.join(" and ")} data.
        </p>
      </SubSection>
    </Section>
  );
}

// ================== Shared components ====================

function ScoreColumn({
  color,
  mean,
}: {
  color: string;
  mean: number;
  n: number;
  label: string;
}) {
  const animated = useCountUp(mean, 1400, 2);
  const heightPct = (mean / 100) * 100;
  return (
    <div
      className="relative rounded-t-sm transition-all duration-[1400ms] shadow-sm"
      style={{
        height: `${heightPct}%`,
        width: 64,
        backgroundColor: color,
      }}
    >
      <div
        className="absolute left-1/2 -translate-x-1/2 -top-7 text-lg font-semibold tabular-nums whitespace-nowrap"
        style={{ color }}
      >
        {animated.toFixed(2)}
      </div>
    </div>
  );
}

function StatRow({
  label,
  withVal,
  withoutVal,
}: {
  label: string;
  withVal?: number;
  withoutVal?: number;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase text-slate-400 font-medium tracking-wider">
        {label}
      </div>
      <div className="flex gap-3 mt-1 text-sm tabular-nums">
        <div className="flex-1">
          <div className="text-[11px] text-slate-500">With</div>
          <div className="font-semibold text-slate-900">{withVal?.toFixed(2) ?? "n/a"}</div>
        </div>
        <div className="flex-1">
          <div className="text-[11px] text-slate-500">Without</div>
          <div className="font-semibold text-slate-700">{withoutVal?.toFixed(2) ?? "n/a"}</div>
        </div>
      </div>
    </div>
  );
}

function PerCaseBar({
  color,
  value,
  scaleMax,
}: {
  color: string;
  value: number;
  scaleMax: number;
}) {
  const animated = useCountUp(value, 1400, 2);
  const heightPct = Math.min(100, (value / scaleMax) * 100);
  return (
    <div
      className="relative rounded-t-sm transition-all duration-[1400ms] shadow-sm"
      style={{ height: `${heightPct}%`, width: 64, backgroundColor: color }}
    >
      <div
        className="absolute left-1/2 -translate-x-1/2 -top-6 text-base font-semibold tabular-nums whitespace-nowrap"
        style={{ color }}
      >
        {animated.toFixed(2)}
      </div>
    </div>
  );
}

function Table({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-300">
            {columns.map((c, i) => (
              <th
                key={c}
                className={`py-2 px-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium ${
                  i > 0 ? "text-right tabular-nums" : ""
                }`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-b border-slate-100 last:border-b-0">
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={`py-2 px-3 text-slate-800 ${
                    ci > 0 ? "text-right tabular-nums font-mono text-[13px]" : ""
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ================== Loading / error ====================

function LoadingState() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white text-slate-900">
      <div className="text-center">
        <Loader2 className="animate-spin mx-auto mb-3 text-slate-400" size={28} />
        <div className="text-sm text-slate-600">Loading study data…</div>
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <div className="max-w-xl bg-white rounded-md border border-red-200 p-6">
        <AlertTriangle className="text-red-500 mb-3" size={24} />
        <div className="text-lg font-semibold text-slate-900 mb-2">Could not load study data</div>
        <div className="text-sm text-slate-600 mb-4">{message}</div>
        <div className="text-xs text-slate-500 bg-slate-50 rounded-md p-3 border border-slate-200">
          The study bundle is served from S3 by{" "}
          <code className="font-mono bg-white px-1">/api/manchester-study-data</code>. Check that
          the server can read{" "}
          <code className="font-mono bg-white px-1">manchester_study_data.json</code> in the
          secure file transfer bucket.
        </div>
      </div>
    </div>
  );
}
